import { useId } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Label } from 'cpool-components/general/Label';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useCountryOptions } from '~/features/leadDetails/components/Overview/CustomerData/shared/useDropdownOptions';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    antragsteller: 1 | 2;
    addressKind: 'anschrift' | 'vorherigeAnschrift';
};

const Land: React.FC<Props> = ({ addressKind, antragsteller }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { control } = useFormContext<SchemaLivingSituationForm>();
    const { t } = useTranslation();
    const countryOptions = useCountryOptions();
    const labelId = useId();

    return (
        <FormField<SchemaLivingSituationForm, string | null>
            control={control}
            name={`antragsteller${antragsteller}.wohnsituation.${addressKind}.land`}
        >
            {({ error, onChange, value }) => (
                <div className="flex items-start gap-2.5">
                    <Label className="h-8 w-[175px] shrink-0 justify-start" id={labelId}>
                        {t('LIVING_SITUATION_DATA.COUNTRY')}
                    </Label>
                    <DropdownMenu
                        aria-labelledby={labelId}
                        data-testid={`antragsteller${antragsteller}.wohnsituation.${addressKind}.land`}
                        options={countryOptions}
                        value={value ?? undefined}
                        onChange={(newValue) => onChange(newValue as string)}
                        placeholder=""
                        triggerClassName="w-full justify-between"
                        className="min-w-0 w-full"
                        disabled={
                            !canExecuteApplicantCommand({
                                applicant: antragsteller,
                                commandName:
                                    addressKind === 'vorherigeAnschrift'
                                        ? LOAN_REQUEST_COMMANDS.changeApplicantPreviousAddress
                                        : LOAN_REQUEST_COMMANDS.changeApplicantAddress,
                                permissions,
                            })
                        }
                        error={error}
                        searchable
                        scrollable
                    />
                </div>
            )}
        </FormField>
    );
};

export default Land;
